import React, { useEffect, useRef } from "react";
import { BRAND, I18N, MOCK_VIDEO_IDS } from "../../mock";
import { Button } from "../../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { CheckCircle, Trophy, Globe2, Briefcase, Star } from "lucide-react";

const iconMap = {
  incentive: Trophy,
  missions: Briefcase,
  dmc: Globe2,
  awards: Star,
  custom: CheckCircle,
};

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.remove("opacity-0", "translate-y-4");
            e.target.classList.add("opacity-100", "translate-y-0");
          }
        });
      },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
};

export const Hero = ({ lang }) => {
  const t = I18N[lang];
  return (
    <section id="top" className="relative h-[72vh] md:h-[84vh] min-h-[560px] w-full overflow-hidden bg-hero-award scroll-mt-32">
      {/* Content */}
      <div className="relative h-full flex items-end md:items-center">
        <div className="max-w-[1200px] mx-auto px-4 md:px-5 pb-10 md:pb-0 w-full">
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8 md:p-10 max-w-4xl shadow-[0_8px_50px_rgba(0,0,0,0.45)]">
            <h1 className="text-white text-3xl sm:text-4xl md:text-5xl font-semibold leading-tight">{t.hero.title}</h1>
            <p className="text-white/85 mt-4 text-base sm:text-lg md:text-xl max-w-3xl">{t.hero.subtitle}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild className="rounded-none border border-[#D4AF37] text-[#D4AF37] bg-transparent hover:bg-[#D4AF37] hover:text-black"><a href="#lead">{t.hero.cta}</a></Button>
              <Button asChild className="rounded-none bg-[#D4AF37] text-black hover:bg-[#c19a2f]"><a href={BRAND.whatsapp} target="_blank" rel="noreferrer">WhatsApp</a></Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const About = ({ lang }) => {
  const t = I18N[lang];
  const ref = useReveal();
  return (
    <section id="about" ref={ref} className="opacity-0 translate-y-4 transition-all duration-700 ease-out bg-black scroll-mt-32">
      <div className="max-w-[1200px] mx-auto px-4 md:px-5 py-14 md:py-24">
        <h2 className="text-[#D4AF37] text-xs sm:text-sm tracking-[0.2em] uppercase">{t.about.title}</h2>
        <p className="text-white mt-3 sm:mt-4 text-lg sm:text-xl leading-relaxed max-w-3xl">{t.about.text}</p>
      </div>
    </section>
  );
};

export const Services = ({ lang }) => {
  const t = I18N[lang];
  const ref = useReveal();
  return (
    <section id="services" ref={ref} className="opacity-0 translate-y-4 transition-all duration-700 ease-out bg-carbon scroll-mt-32">
      <div className="max-w-[1200px] mx-auto px-4 md:px-5 py-14 md:py-24">
        <h2 className="text-white text-2xl sm:text-3xl font-semibold">{t.services.title}</h2>
        <div className="mt-8 md:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {t.services.items.map((s) => {
            const Icon = iconMap[s.key] || CheckCircle;
            return (
              <div key={s.key} className="group border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] transition-colors rounded-xl p-5 md:p-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-md bg-[#D4AF37]/10 text-[#D4AF37]"><Icon size={20} /></div>
                  <h3 className="text-white text-base md:text-lg font-medium">{s.title}</h3>
                </div>
                <p className="text-white/70 mt-3 text-sm md:text-base">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const WhyUs = ({ lang }) => {
  const t = I18N[lang];
  const ref = useReveal();
  return (
    <section id="why" ref={ref} className="opacity-0 translate-y-4 transition-all duration-700 ease-out bg-black scroll-mt-32">
      <div className="max-w-[1200px] mx-auto px-4 md:px-5 py-14 md:py-24">
        <h2 className="text-white text-2xl sm:text-3xl font-semibold">{t.why.title}</h2>
        <ul className="grid md:grid-cols-2 gap-3 md:gap-4 mt-6 md:mt-8">
          {t.why.bullets.map((b, idx) => (
            <li key={idx} className="flex items-start gap-3 border border-white/10 rounded-xl p-4 bg-white/[0.02]"><CheckCircle className="text-[#D4AF37] mt-1" size={20} /><span className="text-white/90 text-sm md:text-base">{b}</span></li>
          ))}
        </ul>
        <div className="mt-8 md:mt-10">
          <h3 className="text-[#D4AF37] uppercase tracking-widest text-xs sm:text-sm">FAQ</h3>
          <Accordion type="single" collapsible className="mt-3 md:mt-4">
            <AccordionItem value="item-1"><AccordionTrigger className="text-white">Como funciona um programa de incentivo?</AccordionTrigger><AccordionContent className="text-white/80">Estruturamos objetivos, regras de elegibilidade e premiações. Cuidamos de toda a operação da viagem, do planejamento à execução.</AccordionContent></AccordionItem>
            <AccordionItem value="item-2"><AccordionTrigger className="text-white">Qual é o prazo ideal para planejamento?</AccordionTrigger><AccordionContent className="text-white/80">Recomendamos 90 a 120 dias para grupos, variando conforme destino e temporada.</AccordionContent></AccordionItem>
            <AccordionItem value="item-3"><AccordionTrigger className="text-white">Atendem grupos internacionais?</AccordionTrigger><AccordionContent className="text-white/80">Sim. Atuamos com rede global de parceiros (DMCs) e operação local em diversos destinos.</AccordionContent></AccordionItem>
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export const Videos = ({ lang }) => {
  const t = I18N[lang];
  const ref = useReveal();
  return (
    <section id="videos" ref={ref} className="opacity-0 translate-y-4 transition-all duration-700 ease-out bg-solid scroll-mt-32">
      <div className="max-w-[1200px] mx-auto px-4 md:px-5 py-14 md:py-24">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <h2 className="text-white text-2xl sm:text-3xl font-semibold">{t.videos.title}</h2>
          <a className="text-[#D4AF37] hover:underline" href={BRAND.youtubeChannelUrl} target="_blank" rel="noreferrer">YouTube</a>
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 mt-6 md:mt-8">
          {MOCK_VIDEO_IDS.map((id) => (
            <div key={id} className="aspect-video rounded-xl overflow-hidden border border-white/10 bg-black">
              <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${id}?rel=0&modestbranding=1`} title={`Video ${id}`} frameBorder="0" loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const LeadForm = ({ lang }) => {
  const t = I18N[lang];
  const containerId = "formulario-de-cotacao-82cc1c9b478164a6066b";

  useEffect(() => {
    const existing = document.getElementById("rdstation-script");
    if (!existing) {
      const s = document.createElement("script");
      s.id = "rdstation-script";
      s.src = "https://d335luupugsy2.cloudfront.net/js/rdstation-forms/stable/rdstation-forms.min.js";
      s.async = true;
      document.body.appendChild(s);
      s.onload = () => { new window.RDStationForms(containerId, "UA-258277586-1").createForm(); };
    } else {
      try { new window.RDStationForms(containerId, "UA-258277586-1").createForm(); } catch (e) {}
    }
  }, []);

  const ref = useReveal();
  return (
    <section id="lead" ref={ref} className="opacity-0 translate-y-4 transition-all duration-700 ease-out bg-solid scroll-mt-32">
      <div className="max-w-[1200px] mx-auto px-4 md:px-5 py-14 md:py-24">
        <h2 className="text-white text-2xl sm:text-3xl font-semibold">{t.lead.title}</h2>
        <p className="text-white/70 mt-2 text-sm md:text-base">{t.lead.subtitle}</p>
        <div className="mt-6 bg-white/5 border border-white/10 rounded-xl p-3 md:p-4">
          <div className="max-w-[720px] mx-auto w-full">
            <div role="main" id={containerId}></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const FinalCTA = ({ lang }) => {
  const t = I18N[lang];
  const ref = useReveal();
  return (
    <section ref={ref} className="opacity-0 translate-y-4 transition-all duration-700 ease-out bg-black scroll-mt-32">
      <div className="max-w-[1200px] mx-auto px-4 md:px-5 py-14 md:py-24 text-center">
        <h2 className="text-white text-xl sm:text-2xl md:text-3xl font-semibold max-w-3xl mx-auto">{t.finalCta.title}</h2>
        <div className="mt-5 md:mt-6 flex items-center justify-center gap-3">
          <Button asChild className="rounded-none bg-[#D4AF37] text-black hover:bg-[#c19a2f]"><a href={BRAND.whatsapp} target="_blank" rel="noreferrer">WhatsApp</a></Button>
          <Button asChild className="rounded-none border border-[#D4AF37] text-[#D4AF37] bg-transparent hover:bg-[#D4AF37] hover:text-black"><a href="#lead">{t.finalCta.cta}</a></Button>
        </div>
      </div>
    </section>
  );
};

export const Footer = ({ lang }) => {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-black section-divider">
      <div className="max-w-[1200px] mx-auto px-4 md:px-5 py-10 grid gap-6 text-white/80 md:grid-cols-3">
        <div>
          <img src={BRAND.logoUrl} alt="MICE Travel" className="h-[120px] w-auto ml-[1cm]" />
          <p className="mt-4 text-sm">Endereço e contatos conforme site oficial.</p>
          <p className="mt-1 text-sm">E-mail: contato@micetravel.com.br</p>
          <p className="mt-1 text-sm">Telefone: +55 (31) 2102-0709</p>
          <a className="mt-2 inline-block text-[#D4AF37] hover:underline" href={BRAND.whatsapp} target="_blank" rel="noreferrer">WhatsApp</a>
        </div>
        <div>
          <h4 className="text-white font-medium">Links</h4>
          <ul className="mt-2 space-y-2 text-sm">
            <li><a className="hover:underline" href="https://www.micetravel.com.br/" target="_blank" rel="noreferrer">Site oficial</a></li>
            <li><a className="hover:underline" href="#">Política de Privacidade</a></li>
            <li><a className="hover:underline" href="https://www.instagram.com/micetravel/" target="_blank" rel="noreferrer">Instagram</a></li>
            <li><a className="hover:underline" href="https://www.youtube.com/@micetravel" target="_blank" rel="noreferrer">YouTube</a></li>
            <li><a className="hover:underline" href="https://www.linkedin.com/company/mice-travel-show" target="_blank" rel="noreferrer">LinkedIn</a></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-medium">Localização</h4>
          <p className="mt-2 text-sm">Brasil • Operação em destinos globais</p>
          <p className="mt-4 text-xs text-white/50">© {year} MICE Travel ®. Todos os direitos reservados.</p>
        </div>
      </div>
    </footer>
  );
};
